import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type CSVData, csvDownload } from '@/utils/csv-util'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type ColumnId, exportValue } from './jobs-table-columns'

interface ExportableColumnDetails {
  id: ColumnId
  header: string
  visible: boolean
}

/**
 * Builds the CSV export of the filtered jobs for the given columns.
 */
export function createJobsExportAdapter(
  getAllFilteredJobs: () => Promise<SearchableJob[]>,
  getFilteredColumns: () => readonly ExportableColumnDetails[],
  newAlert: AlertFactory,
  createAlertErrorHandler: AlertErrorHandlerFactory,
) {
  const jobsExportData = async () => {
    const jobs = await getAllFilteredJobs()
    const columns = [...getFilteredColumns()]

    // Insert Job ID column after Job Short ID column.
    columns.splice(2, 0, {
      id: 'jobId',
      header: 'Job ID',
      visible: true,
    })

    const header: CSVData = [
      columns
        .filter((column) => column.visible)
        .map((column) =>
          // The short ID is labeled as "Job ID" in the UI.
          column.id === 'jobShortId' ? 'Job Short ID' : column.header,
        )
        .concat('Job specs'),
    ]
    const data: CSVData = jobs.map((job) =>
      columns
        .filter((column) => column.visible)
        .map((column) => exportValue(column.id, job))
        .concat(job.jobScopeItems.join(', ')),
    )

    return header.concat(data)
  }

  return {
    /**
     * Downloads the jobs as a CSV file, reporting progress in an alert.
     */
    downloadCsv: async () => {
      const progress = newAlert().saying('Job exporting in progress').show()
      const csvData = await jobsExportData().catch((err: unknown) => {
        progress.dismiss()
        return createAlertErrorHandler('Exporting jobs failed')(err)
      })

      progress
        .withState('success')
        .saying(
          'Job export completed!',
          'The exported file should begin downloading...',
        )
        .autoDismiss()
      csvDownload(csvData, 'jobs.csv')
    },
  }
}
